import { Alert, Flex, Spin } from 'antd';
import { ReactNode } from 'react';

import { useNavigate, useSearchParams } from '@suntex.core/nav';
import { PageMetadata, useModuleById } from '@suntex.fim/framework';
import { usePlayerDashboardPath } from '@suntex.fim/nav';
import {
  ComingSoon,
  DirectionsButton,
  HelpText,
  useModuleAccess,
  usePlayerContext,
  useUnderConstruction,
} from '@suntex.fim/player';
import { Button } from '@suntex.fim/ui';

import './ModuleLayout.scss';

export interface ModuleLayoutProps {
  children: ReactNode;
  moduleId: number;
  title?: string;
  subtitle?: string;
  isLegacyModule?: boolean;
  alertMessage?: string;
}

export function ModuleLayout({
  children,
  moduleId,
  title,
  subtitle,
  isLegacyModule,
  alertMessage,
}: ModuleLayoutProps) {
  const { isPreview } = usePlayerContext();
  const { isUnderConstruction, isLoading: isUnderConstructionLoading } =
    useUnderConstruction();
  const { isModuleEnabled, isLoading: isAccessLoading } = useModuleAccess();
  const module = useModuleById(moduleId);
  const navigateFromHook = useNavigate();
  const navigate = isLegacyModule
    ? (href: string) => {
        window.location.href = href;
      }
    : navigateFromHook;
  const [searchParams] = useSearchParams();
  const suggestionKey = searchParams.get('suggestionKey');
  const hasSuggestionBypass =
    !!suggestionKey && suggestionKey === module?.slugLink.urlClassic.hash;
  const hubUrl = usePlayerDashboardPath();

  const moduleSlug = module?.slug ?? '';
  const moduleTitle = title ?? module?.description ?? module?.name ?? '';
  const moduleSubtitle = subtitle ?? module?.subTitle ?? '';

  const checkAccess = !isPreview && !hasSuggestionBypass;
  const accessDenied =
    checkAccess && !!moduleSlug && !isModuleEnabled(moduleSlug);

  return (
    <div className="ModuleLayout">
      {<PageMetadata title={moduleTitle} />}

      <Button
        style={{ alignSelf: 'flex-start' }}
        onClick={() => navigate(hubUrl)}
      >
        Back to the hub
      </Button>

      <header>
        {isPreview && <h2>Preview Mode - No stickers will be earned.</h2>}

        <h1 className="title">{moduleTitle}</h1>

        {moduleSubtitle && <h2 className="subtitle">{moduleSubtitle}</h2>}
      </header>

      {isUnderConstructionLoading || (checkAccess && isAccessLoading) ? (
        <Spin />
      ) : moduleSlug && isUnderConstruction(moduleSlug) ? (
        <ComingSoon classicPath={module?.slugLink.urlClassic.url} />
      ) : accessDenied ? (
        <div style={{ padding: '1em' }}>
          <h2>Sorry</h2>
          <p>You do not have access to this module.</p>
        </div>
      ) : (
        children
      )}

      <Flex
        gap={4}
        align="center"
        justify="center"
        style={{ marginTop: '.5em' }}
      >
        {module && <HelpText module={module} />}

        {module?.directionsUrl && (
          <DirectionsButton url={module.directionsUrl} />
        )}
      </Flex>

      {alertMessage && (
        <Flex justify="center" style={{ marginTop: '.5em' }}>
          <Alert message={alertMessage} type="info" showIcon />
        </Flex>
      )}
    </div>
  );
}
